import React, { useEffect, useState, useRef, lazy, memo, Component } from 'react';
import { CheckIcon, StarIcon, ChevronDownIcon, XIcon, ChevronLeftIcon, ChevronRightIcon, PlayIcon, PauseIcon } from 'lucide-react';
import { Footer } from '../components/Footer';
import { AudioPlayer } from '../components/AudioPlayer';
// ─── Checkout URL builder ────────────────────────────────────────────────────
const buildUrl = (template: string) => {
  const params = new URLSearchParams(window.location.search);
  const fbclid = params.get('fbclid');
  const clickId = params.get('click_id') || sessionStorage.getItem('cf_click_id') || localStorage.getItem('cf_click_id');
  let url = template;
  if (fbclid) {
    url = url.replace('{{fbclid}}', fbclid);
  } else {
    url = url.replace('&fbclid={{fbclid}}', '').replace('fbclid={{fbclid}}', '');
  }
  if (clickId) {
    const separator = url.includes('?') ? '&' : '?';
    // If it's a shopify cart link, ensure 'checkout' is handled correctly
    if (url.includes('/cart/')) {
      const hasCheckout = url.includes('checkout');
      const cleanUrl = url.replace(/[?&]checkout/, '');
      url = cleanUrl + (cleanUrl.includes('?') ? '&' : '?') + 'click_id=' + encodeURIComponent(clickId) + (hasCheckout ? '&checkout' : '');
    } else {
      url = url + separator + 'click_id=' + encodeURIComponent(clickId);
    }
  }
  return url;
};
// ─── Tracking ────────────────────────────────────────────────────────────────
declare global {
  interface Window {
    fbq: (action: string, event: string, params?: any) => void;
  }
}
const track = (_name: string, _price: number) => {
  // AddToCart/InitiateCheckout removed
};
// Fire when pricing section enters viewport
let pricingViewFired = false;
const trackPricingView = () => {
  if (pricingViewFired) return;
  if (sessionStorage.getItem('pricing_section_viewed')) return;
  pricingViewFired = true;
  sessionStorage.setItem('pricing_section_viewed', 'true');
  if (typeof window.fbq === 'function') {
    window.fbq('track', 'ViewContent', {
      content_name: 'Pricing Section — Complete Digital Collection',
      value: 59.97,
      currency: 'USD'
    });
  }
};
// ─── Design tokens ───────────────────────────────────────────────────────────
const GOLD = '#D4AF37';
const NAVY = '#0B1B3D';
const NAVY_ALT = '#0F2347';
// ─── Audio ───────────────────────────────────────────────────────────────────
const AUDIO_SRC = 'https://cdn.shopify.com/s/files/1/0586/4069/5378/files/BIT_Audiobook_Preview.mp3?v=1762440014';
// ─── Mobile audio button (gold theme) ────────────────────────────────────────
const MobileAudioButtonV2: React.FC<{
  src: string;
}> = ({
  src
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };
  return <>
      <audio ref={audioRef} src={src} preload="none" onEnded={() => setIsPlaying(false)} data-id="element-1383" />
      <button type="button" onClick={toggle} className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 shadow-lg ${isPlaying ? 'scale-95' : ''}`} style={{
      backgroundColor: GOLD,
      boxShadow: isPlaying ? '0 0 12px rgba(212,175,55,0.5)' : '0 2px 8px rgba(212,175,55,0.2)'
    }} aria-label={isPlaying ? 'Pause' : 'Play'} data-id="element-1384">
        {isPlaying ? <PauseIcon className="h-5 w-5" style={{
        color: NAVY
      }} data-id="element-1385" /> : <PlayIcon className="h-5 w-5 ml-0.5" style={{
        color: NAVY
      }} data-id="element-1386" />}
      </button>
    </>;
};
// ─── Collector bundle images ─────────────────────────────────────────────────
const BUNDLE_IMAGES = ['https://cdn.magicpatterns.com/uploads/2mCBcB1vUDxS6qefycewPD/TIME_TRAVELLERS_COLLECTOR_BUNDLED.png', 'https://cdn.magicpatterns.com/uploads/7BQJLvNZKyzy2Jn4rjWLvk/bit_audiobook.png', 'https://cdn.magicpatterns.com/uploads/8av42GqLUN9WRwXAhxHRBi/Generated_Image_November_18,_2025_-_3_14PM.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/i3DKchuX8RMmzYrqAhpK13/foam-trucker-hat-black-one-size-right-front-6914c3a4781f3.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/rbwPKtwrCHTaFNqFeaH2fA/enhanced-matte-paper-framed-poster-%28in%29-white-10x10-front-6914bc8d8b378.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/2eQBo88LwYLe7TLxEHMDQe/white-glossy-mug-white-11-oz-handle-on-left-6914d9d1888e4.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/dsPxuWW7a4Xja3nWKMPU8M/all-over-print-tote-bag-yellow-15x15-mockup-69123afa061d2.jpg'];
// ─── Image lightbox ──────────────────────────────────────────────────────────
const ImageLightbox = ({
  images,
  startIndex,
  onClose
}: {
  images: string[];
  startIndex: number;
  onClose: () => void;
}) => {
  const [idx, setIdx] = useState(startIndex);
  const prev = () => setIdx(i => i === 0 ? images.length - 1 : i - 1);
  const next = () => setIdx(i => i === images.length - 1 ? 0 : i + 1);
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4" onClick={onClose} data-id="element-1387">
      <div className="relative w-full max-w-lg" onClick={e => e.stopPropagation()} data-id="element-1388">
        <button onClick={onClose} className="absolute -top-10 right-0 text-white/70 hover:text-white transition-colors" aria-label="Close" data-id="element-1389">
          <XIcon className="h-7 w-7" data-id="element-1390" />
        </button>
        <div className="rounded-xl overflow-hidden shadow-2xl bg-black" style={{
        aspectRatio: '1/1'
      }} data-id="element-1391">
          <img src={images[idx]} alt={`Bundle item ${idx + 1}`} className="w-full h-full object-contain" decoding="async" data-id="element-1392" />
        </div>
        <button onClick={prev} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full transition-all" aria-label="Previous" data-id="element-1393">
          <ChevronLeftIcon className="h-5 w-5" data-id="element-1394" />
        </button>
        <button onClick={next} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full transition-all" aria-label="Next" data-id="element-1395">
          <ChevronRightIcon className="h-5 w-5" data-id="element-1396" />
        </button>
        <div className="flex justify-center gap-1.5 mt-3" data-id="element-1397">
          {images.map((_, i) => <button key={i} onClick={() => setIdx(i)} className="rounded-full transition-all duration-200" style={{
          width: i === idx ? '20px' : '8px',
          height: '8px',
          backgroundColor: i === idx ? GOLD : 'rgba(255,255,255,0.3)'
        }} data-id="element-1398" />)}
        </div>
      </div>
    </div>;
};
// ─── Shared CTA button ───────────────────────────────────────────────────────
const GoldButton = ({
  href,
  onClick,
  children,
  small = false
}: {
  href: string;
  onClick?: () => void;
  children: React.ReactNode;
  small?: boolean;
}) => <a href={href} onClick={onClick} className="cta block w-full text-center font-bold tracking-wide rounded-lg transition-all duration-200 active:scale-[0.98] no-underline hover:brightness-110 shadow-lg" style={{
  minHeight: small ? '44px' : '52px',
  padding: small ? '10px 16px' : '14px 24px',
  fontSize: small ? '14px' : '16px',
  backgroundColor: GOLD,
  color: '#0B1B3D',
  fontFamily: 'Inter, sans-serif',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}} data-id="element-1399">
    {children}
  </a>;
// ─── Gold checkmark list item ─────────────────────────────────────────────────
const GoldCheck = ({
  children
}: {
  children: React.ReactNode;
}) => <li className="flex items-start gap-3" data-id="element-1400">
    <span style={{
    color: GOLD,
    flexShrink: 0,
    marginTop: '3px'
  }} data-id="element-1401">
      ✦
    </span>
    <span className="text-white/90 text-base leading-relaxed" style={{
    fontFamily: 'Inter, sans-serif'
  }} data-id="element-1402">
      {children}
    </span>
  </li>;
const BumpItem = ({
  text
}: {
  text: string;
}) => <li className="flex items-start gap-2" data-id="element-1403">
    <span style={{
    color: GOLD,
    flexShrink: 0,
    fontSize: '9px',
    marginTop: '5px'
  }} data-id="element-1404">
      ✦
    </span>
    <span className="text-xs leading-snug" style={{
    color: 'rgba(255,255,255,0.78)',
    fontFamily: 'Inter, sans-serif'
  }} data-id="element-1405">
      {text}
    </span>
  </li>;
// ─── SECTION 1: HERO ─────────────────────────────────────────────────────────
const HeroV2 = () => <section style={{
  backgroundColor: NAVY
}} className="pt-4 pb-6 md:pt-6 md:pb-8" data-id="element-1406">
    <div className="container mx-auto px-6 md:px-10 flex flex-col md:flex-row md:items-center gap-4 md:gap-8 max-w-6xl" data-id="element-1407">
      <div className="w-full md:w-1/2 text-center md:text-left" data-id="element-1408">
        <p className="text-xs font-semibold uppercase tracking-widest mb-2 md:mb-3" style={{
        color: GOLD,
        fontFamily: 'Inter, sans-serif',
        letterSpacing: '0.12em'
      }} data-id="element-1409">
          For the generation that lived it first.
        </p>
        <h1 className="text-[26px] md:text-4xl lg:text-5xl font-bold leading-tight mb-3 md:mb-4 text-white" style={{
        fontFamily: "'Playfair Display', Georgia, serif",
        lineHeight: 1.15
      }} data-id="element-1410">
          It Was Never Just a Movie.{' '}
          <span className="block" data-id="element-1411">It Became Part of Who You Are.</span>
        </h1>
        <p className="text-sm md:text-base text-white/75 leading-relaxed mb-3 md:mb-4 max-w-[560px] mx-auto md:mx-0" style={{
        fontFamily: 'Inter, sans-serif'
      }} data-id="element-1412">
          Forty years of cultural impact. Finally documented the way it
          deserves, for the fans who never stopped caring.
        </p>

        {/* Mobile image — constrained to keep it compact while preserving ratio */}
        <div className="md:hidden mb-3 flex justify-center" data-id="element-1413">
          <div className="rounded-xl overflow-hidden shadow-2xl w-[90%]" data-id="element-1414">
            <img src="https://cdn.magicpatterns.com/uploads/drsCGCcawJQmxdkJUsQyKs/Holiday_Edition_Digital_Set.png" alt="Complete 40th Anniversary Digital Collection" className="w-full object-cover" style={{
            aspectRatio: '1/1'
          }} loading="eager" fetchpriority="high" decoding="async" width="600" height="600" data-id="element-1415" />
          </div>
        </div>

        <ul className="space-y-1.5 mb-4 md:mb-5 text-left max-w-[480px] mx-auto md:mx-0" data-id="element-1416">
          <GoldCheck data-id="element-1417">
            All four 40th Anniversary volumes, nothing cut, nothing summarized
          </GoldCheck>
          <GoldCheck data-id="element-1418">
            Cultural analysis, production history, 40 reflections, philosophical
            legacy
          </GoldCheck>
          <GoldCheck data-id="element-1419">Yours instantly, no waiting.</GoldCheck>
        </ul>

        <div className="max-w-[480px] mx-auto md:mx-0" data-id="element-1420">
          <GoldButton href="#pricing-top" data-id="element-1421">
            CLAIM YOUR COMPLETE COLLECTION NOW →
          </GoldButton>
        </div>

        {/* Desktop Audio Player */}
        <div className="hidden md:block mt-4 max-w-[480px] mx-auto md:mx-0 px-4 py-3 rounded-lg" style={{
        background: 'rgba(11,27,61,0.8)',
        border: `1px solid ${GOLD}40`,
        boxShadow: `0 0 15px rgba(212,175,55,0.1)`
      }} data-id="element-1422">
          <div className="flex items-center gap-2 mb-2" data-id="element-1423">
            <span className="text-base" data-id="element-1424">🎧</span>
            <span className="text-sm font-semibold" style={{
            color: `${GOLD}dd`,
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1425">
              Press Play: Hear How It All Began (1 min preview).
            </span>
          </div>
          <AudioPlayer src={AUDIO_SRC} theme="gold" data-id="element-1426" />
          <p className="text-xs mt-2 text-center" style={{
          color: 'rgba(255,255,255,0.35)',
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1427">
            Hear the ticking clocks that started everything.
          </p>
        </div>

        {/* Mobile Audio Player */}
        <div className="md:hidden flex items-center gap-3 mt-3 px-3 py-2.5 rounded-lg" style={{
        background: 'rgba(11,27,61,0.8)',
        border: `1px solid ${GOLD}40`,
        boxShadow: `0 0 15px rgba(212,175,55,0.1)`
      }} data-id="element-1428">
          <span className="text-base" data-id="element-1429">🎧</span>
          <span className="text-xs font-semibold flex-1" style={{
          color: `${GOLD}dd`,
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1430">
            Press Play: Hear How It All Began (1 min preview).
          </span>
          <MobileAudioButtonV2 src={AUDIO_SRC} data-id="element-1431" />
        </div>
      </div>

      {/* Desktop image */}
      <div className="hidden md:flex md:w-1/2 justify-center items-center" data-id="element-1432">
        <div className="w-full max-w-md rounded-2xl overflow-hidden shadow-[0_0_60px_rgba(212,175,55,0.15)]" data-id="element-1433">
          <img src="https://cdn.magicpatterns.com/uploads/drsCGCcawJQmxdkJUsQyKs/Holiday_Edition_Digital_Set.png" alt="Complete 40th Anniversary Digital Collection" className="w-full object-cover" loading="eager" fetchpriority="high" decoding="async" width="600" height="600" data-id="element-1434" />
        </div>
      </div>
    </div>
  </section>;
// ─── SECTION 2: PROBLEM AGITATION ────────────────────────────────────────────
const ProblemV2 = () => <section style={{
  backgroundColor: NAVY,
  paddingTop: '48px',
  paddingBottom: '48px'
}} className="md:py-20 border-t border-white/10" data-id="element-1435">
    <div className="container mx-auto px-6 md:px-10 max-w-2xl text-center" data-id="element-1436">
      <h2 className="text-[26px] md:text-4xl font-bold text-white mb-8 leading-tight" style={{
      fontFamily: "'Playfair Display', Georgia, serif"
    }} data-id="element-1437">
        Every Anniversary Release Feels the Same. And You Know It.
      </h2>
      <div className="text-left space-y-6" data-id="element-1438">
        <p className="text-base md:text-lg text-white/70 leading-relaxed" style={{
        fontFamily: 'Inter, sans-serif'
      }} data-id="element-1439">
          The Blu-ray remaster. The poster in a tube. The plastic 'collector's
          edition.'
          <br data-id="element-1440" />
          <br data-id="element-1441" />
          Every year, something new gets released. And every year, it feels like
          they missed the point entirely.
        </p>
        <p className="text-base md:text-lg leading-relaxed" style={{
        fontFamily: 'Inter, sans-serif'
      }} data-id="element-1442">
          <span className="text-white font-medium" data-id="element-1443">
            Because nobody is treating it like what it actually is:{' '}
          </span>
          <span style={{
          color: GOLD
        }} className="font-semibold" data-id="element-1444">
            a 40-year cultural document that shaped how an entire generation
            understands ambition, risk, and the future.
          </span>
        </p>
        <p className="text-base md:text-lg text-white/70 leading-relaxed" style={{
        fontFamily: 'Inter, sans-serif'
      }} data-id="element-1445">
          This collection was built differently. Not as merchandise, as a
          cultural archive. Assembled the way a historian approaches a defining
          moment: from the inside out.
        </p>
        <p className="text-sm md:text-base italic" style={{
        color: GOLD,
        fontFamily: 'Inter, sans-serif'
      }} data-id="element-1446">
          The film isn't aging. The culture around it is. Someone had to
          document it properly.
        </p>
      </div>
    </div>
  </section>;
// ─── SECTION 3: WHAT'S INSIDE ────────────────────────────────────────────────
const BookBlock = ({
  images,
  title,
  body
}: {
  images: [string, string, string];
  title: string;
  body: string;
}) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(0);
  const openImg = (e: React.MouseEvent, i: number) => {
    e.stopPropagation();
    setLightboxIdx(i);
    setLightboxOpen(true);
  };
  return <div className="mb-12 md:mb-16" data-id="element-1447">
      <div className="flex gap-2 mb-5" data-id="element-1448">
        {images.map((src, i) => <div key={i} className="flex-1 rounded-lg overflow-hidden shadow-md border border-white/10 cursor-pointer group relative" onClick={e => openImg(e, i)} data-id="element-1449">
            <img src={src} alt={`${title}, page ${i + 1}`} className="w-full object-cover transition-opacity duration-200 group-hover:opacity-80" style={{
          aspectRatio: '3/4'
        }} loading="lazy" decoding="async" data-id="element-1450" />
            {/* zoom hint */}
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200" data-id="element-1451">
              <div className="bg-black/50 rounded-full p-1.5" data-id="element-1452">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-1453">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0zm-6-3v6m-3-3h6" data-id="element-1454" />
                </svg>
              </div>
            </div>
          </div>)}
      </div>
      <h3 className="text-xl md:text-2xl font-bold mb-2" style={{
      color: GOLD,
      fontFamily: "'Playfair Display', Georgia, serif"
    }} data-id="element-1455">
        {title}
      </h3>
      <p className="text-base text-white/65 leading-relaxed" style={{
      fontFamily: 'Inter, sans-serif'
    }} data-id="element-1456">
        {body}
      </p>
      {lightboxOpen && <ImageLightbox images={images as unknown as string[]} startIndex={lightboxIdx} onClose={() => setLightboxOpen(false)} data-id="element-1457" />}
    </div>;
};
const WhatsInsideV2 = () => <section style={{
  backgroundColor: NAVY_ALT,
  paddingTop: '48px',
  paddingBottom: '48px'
}} className="md:py-20" data-id="element-1458">
    <div className="container mx-auto px-6 md:px-10 max-w-4xl" data-id="element-1459">
      <h2 className="text-[26px] md:text-4xl font-bold text-white text-center mb-12" style={{
      fontFamily: "'Playfair Display', Georgia, serif"
    }} data-id="element-1460">
        The Complete 40th Anniversary Archive, 4 Volumes
      </h2>
      <BookBlock images={['https://cdn.magicpatterns.com/uploads/tq45Thpd45LDyHLHFRyHb4/image.png', 'https://cdn.magicpatterns.com/uploads/xAZSGuTLPmciaBSXGgJTHj/image.png', 'https://cdn.magicpatterns.com/uploads/g3EfcxHfyFE6HpXeHQ2aZA/image.png']} title="Back In Time, The Cultural Analysis" body="190 pages. Why the film shaped American cultural identity from 1985 to today. Not a summary. A documented analysis." data-id="element-1461" />
      <BookBlock images={['https://cdn.magicpatterns.com/uploads/rerwYoQ8gs22KNkXTHs5Gf/image.png', 'https://cdn.magicpatterns.com/uploads/rLQyuhnh2qhC64hG3pEah5/image.png', 'https://cdn.magicpatterns.com/uploads/vxi7ma652Y1wW88rpSxwZe/image.png']} title="Behind the Clock Tower, The Production Story" body="87 pages. The production stories that never made it into DVD extras. The decisions that almost broke the film." data-id="element-1462" />
      <BookBlock images={['https://cdn.magicpatterns.com/uploads/gzWz855tfQDUeagTytQYCe/image.png', 'https://cdn.magicpatterns.com/uploads/3NyPkbzUAwACdE2zMZ8MuJ/image.png', 'https://cdn.magicpatterns.com/uploads/aN6H3njHZtV8jQu2BhuyAk/image.png']} title="Great Scott! 40 Reflections for 40 Years" body="87 pages. 40 voices across 40 years on why this film changed how they see the world." data-id="element-1463" />
      <BookBlock images={['https://cdn.magicpatterns.com/uploads/vRUrFezUsXr4NeCCTTcL6b/image.png', 'https://cdn.magicpatterns.com/uploads/7sMQijfdPvvuxFFNxak5uH/image.png', 'https://cdn.magicpatterns.com/uploads/4LoVuiUpCZ3tw4hi51F4TM/image.png']} title="Time. Imagination. Destiny. The Philosophical Legacy" body="71 pages. The essay on identity, free will, and why the ending has haunted you for decades." data-id="element-1464" />
      <p className="text-center text-white/50 italic text-sm mt-4" style={{
      fontFamily: 'Inter, sans-serif'
    }} data-id="element-1465">
        Delivered instantly. Readable on any device.
      </p>
    </div>
  </section>;
// ─── SECTION 4: SOCIAL PROOF ──────────────────────────────────────────────────
const SocialProofV2 = () => <section style={{
  backgroundColor: NAVY,
  paddingTop: '48px',
  paddingBottom: '48px'
}} className="md:py-20" data-id="element-1466">
    <div className="container mx-auto px-6 md:px-10 max-w-2xl text-center" data-id="element-1467">
      <h2 className="text-[26px] md:text-4xl font-bold text-white mb-10" style={{
      fontFamily: "'Playfair Display', Georgia, serif"
    }} data-id="element-1468">
        12,847 Fans Already Own This Collection.
      </h2>
      <div className="rounded-2xl p-8 md:p-10 shadow-xl" style={{
      backgroundColor: '#F9F6EF',
      maxWidth: '600px',
      margin: '0 auto'
    }} data-id="element-1469">
        <div className="flex justify-center gap-1 mb-5" data-id="element-1470">
          {[...Array(5)].map((_, i) => <StarIcon key={i} className="h-5 w-5" style={{
          color: GOLD,
          fill: GOLD
        }} data-id="element-1471" />)}
        </div>
        <blockquote className="text-gray-800 text-base md:text-lg leading-relaxed mb-6 italic" style={{
        fontFamily: "'Playfair Display', Georgia, serif"
      }} data-id="element-1472">
          "I've bought every anniversary release since 1995. This is the first
          one that actually made me stop and think. Not just remember,
          understand. There's a difference."
        </blockquote>
        <p className="text-gray-500 text-sm font-medium" style={{
        fontFamily: 'Inter, sans-serif'
      }} data-id="element-1473">
          Marcus R., San Diego, CA
        </p>
      </div>
    </div>
  </section>;
// ─── SECTION 5: PURCHASE BLOCK ────────────────────────────────────────────────
const CHECKOUT_DIGITAL = 'https://legendaryreads.com/cart/42231842701394:1?checkout&fbclid={{fbclid}}';
const CHECKOUT_PREMIUM = 'https://legendaryreads.com/cart/42285346947154:1?checkout&fbclid={{fbclid}}';
const CHECKOUT_BUNDLE = 'https://legendaryreads.com/cart/42285352288338:1?checkout&fbclid={{fbclid}}';
const CHECKOUT_BASIC = 'https://legendaryreads.com/cart/42185022439506:1?checkout&fbclid={{fbclid}}';
const BASE_PRICE = 59.97;
const BUMP1_PRICE = 35;
const BUMP2_PRICE_STANDALONE = 220;
const BUMP2_PRICE_WITH_BUMP1 = 185;
const BUMP1_ITEMS = ['Everything in the Complete Digital Collection', 'Back In Time — Premium Hardcover Edition', 'Delivery in 12 days'];
const BUMP2_ITEMS = ['Everything in the Complete Digital Collection', 'All 4 volumes in Paperback Edition', 'Back In Time Audiobook Experience', 'Hill Valley 1985 Limited Vintage Trucker Hat', "Flux Energy Core Collector's Framed Print — 10×10 Limited Edition", 'Time Machine Heritage Tote', '40 Years Travelling Through Time Commemorative Mug', 'Fast Delivery'];
const PurchaseV2 = () => {
  const [bump1, setBump1] = useState(false);
  const [bump2, setBump2] = useState(false);
  const [bump2Expanded, setBump2Expanded] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxStart, setLightboxStart] = useState(0);
  const [bump1LightboxOpen, setBump1LightboxOpen] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const bump2Price = bump1 ? BUMP2_PRICE_WITH_BUMP1 : BUMP2_PRICE_STANDALONE;
  const total = BASE_PRICE + (bump1 ? BUMP1_PRICE : 0) + (bump2 ? bump2Price : 0);
  const checkoutUrl = buildUrl(bump2 ? CHECKOUT_BUNDLE : bump1 ? CHECKOUT_PREMIUM : CHECKOUT_DIGITAL);
  const handleCta = () => {
    const name = bump2 ? "Time Traveler's Collector Bundle" : bump1 ? 'Premium Hardcover Bundle' : 'Complete Digital Collection';
    track(name, total);
  };
  const openLightbox = (e: React.MouseEvent, idx = 0) => {
    e.stopPropagation();
    setLightboxStart(idx);
    setLightboxOpen(true);
  };
  // Fire ClickMagick + fbq when pricing section enters viewport
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        trackPricingView();
        observer.disconnect();
      }
    }, {
      threshold: 0.25
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return <section ref={sectionRef} style={{
    backgroundColor: NAVY_ALT,
    paddingTop: '32px',
    paddingBottom: '48px',
    scrollMarginTop: '64px'
  }} className="md:py-20" id="pricing-top" data-id="element-1474">
      <div className="container mx-auto px-4 md:px-10 max-w-2xl" data-id="element-1475">
        <h2 className="text-[22px] md:text-4xl font-bold text-white text-center mb-4 md:mb-10" style={{
        fontFamily: "'Playfair Display', Georgia, serif"
      }} data-id="element-1476">
          This Is Where You Secure the Legacy
        </h2>

        {/* ── MAIN PRODUCT CARD ── */}
        <div className="rounded-xl mb-3 shadow-[0_8px_40px_rgba(0,0,0,0.5)]" style={{
        backgroundColor: '#FFFFFF'
      }} data-id="element-1477">
          {/* MOBILE: compact with image */}
          <div className="md:hidden px-4 py-4" data-id="element-1478">
            <p className="text-[11px] font-bold text-center uppercase tracking-widest mb-3" style={{
            color: NAVY,
            fontFamily: 'Inter, sans-serif',
            letterSpacing: '0.06em'
          }} data-id="element-1479">
              The Complete Digital Collection
            </p>
            {/* Image + books side by side */}
            <div className="flex gap-3 items-center mb-3" data-id="element-1480">
              <img src="https://cdn.magicpatterns.com/uploads/drsCGCcawJQmxdkJUsQyKs/Holiday_Edition_Digital_Set.png" alt="Complete Digital Collection" className="rounded-lg shadow-md flex-shrink-0" style={{
              width: '100px',
              height: '100px',
              objectFit: 'cover'
            }} loading="lazy" decoding="async" data-id="element-1481" />
              <div className="flex-1 grid grid-cols-1 gap-y-1" data-id="element-1482">
                {['Back In Time (224 pg)', 'Behind The Clock Tower (87 pg)', 'Great Scott! (87 pg)', 'Time. Imagination. Destiny. (71 pg)'].map(item => <div key={item} className="flex items-start gap-1.5" data-id="element-1483">
                    <span style={{
                  color: GOLD,
                  fontSize: '8px',
                  flexShrink: 0,
                  marginTop: '4px'
                }} data-id="element-1484">
                      ✦
                    </span>
                    <span className="text-[11px] leading-tight" style={{
                  color: '#1a1a2e',
                  fontFamily: 'Inter, sans-serif'
                }} data-id="element-1485">
                      {item}
                    </span>
                  </div>)}
              </div>
            </div>
            {/* Price */}
            <div className="text-center mb-3" data-id="element-1486">
              <span className="line-through text-sm opacity-40 mr-2" style={{
              color: '#333',
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1487">
                $114.97
              </span>
              <span className="font-bold text-[26px] leading-none transition-all duration-300" style={{
              color: NAVY,
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1488">
                ${total.toFixed(2)}
              </span>
              {(bump1 || bump2) && <span className="block text-[10px] text-gray-400 mt-0.5" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1489">
                  w/ selected add-ons
                </span>}
            </div>
            <GoldButton href={checkoutUrl} onClick={handleCta} data-id="element-1490">
              GET MY COLLECTION →
            </GoldButton>
            <p className="text-center text-gray-400 text-[10px] mt-1.5" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1491">
              Instant delivery. Readable on any device.
            </p>
          </div>

          {/* DESKTOP: full layout */}
          <div className="hidden md:block p-10" data-id="element-1492">
            <h3 className="text-2xl font-bold text-center mb-6" style={{
            color: NAVY,
            fontFamily: 'Inter, sans-serif',
            letterSpacing: '0.04em'
          }} data-id="element-1493">
              THE COMPLETE DIGITAL COLLECTION
            </h3>
            <div className="flex justify-center mb-6" data-id="element-1494">
              <img src="https://cdn.magicpatterns.com/uploads/drsCGCcawJQmxdkJUsQyKs/Holiday_Edition_Digital_Set.png" alt="Complete Digital Collection" className="rounded-xl shadow-md" style={{
              maxWidth: '260px',
              width: '100%'
            }} loading="lazy" decoding="async" data-id="element-1495" />
            </div>
            <ul className="space-y-3 mb-6" data-id="element-1496">
              {['Back In Time (224 pages) — The Cultural Analysis', 'Behind the Clock Tower (87 pages) — The Production Story', 'Great Scott! (87 pages) — 40 Reflections for 40 Years', 'Time. Imagination. Destiny. (71 pages) — The Philosophical Legacy'].map(item => <li key={item} className="flex items-start gap-3" data-id="element-1497">
                  <span className="font-bold mt-0.5" style={{
                color: GOLD,
                flexShrink: 0
              }} data-id="element-1498">
                    ✦
                  </span>
                  <span className="text-base leading-relaxed" style={{
                color: '#1a1a2e',
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1499">
                    {item}
                  </span>
                </li>)}
            </ul>
            <div className="text-center mb-2" data-id="element-1500">
              <span className="line-through opacity-40 mr-2 text-lg" style={{
              color: '#333',
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1501">
                $79.97
              </span>
              <span className="font-bold text-3xl transition-all duration-300" style={{
              color: NAVY,
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1502">
                ${total.toFixed(2)}
              </span>
            </div>
            {(bump1 || bump2) && <p className="text-center text-xs mb-4" style={{
            color: '#666',
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1503">
                Includes selected add-ons
              </p>}
            <GoldButton href={checkoutUrl} onClick={handleCta} data-id="element-1504">
              GET MY COLLECTION →
            </GoldButton>
            <p className="text-center text-gray-400 text-xs mt-3" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1505">
              Instant delivery. Readable on any device.
            </p>
          </div>
        </div>

        {/* ── ORDER BUMP 1 ── */}
        <div className="rounded-xl mb-3 overflow-hidden transition-all duration-300" style={{
        border: bump1 ? `3px solid ${GOLD}` : '3px solid rgba(212,175,55,0.4)',
        backgroundColor: bump1 ? 'rgba(212,175,55,0.07)' : '#0a1628',
        boxShadow: bump1 ? `0 0 28px rgba(212,175,55,0.3)` : '0 2px 12px rgba(0,0,0,0.3)'
      }} data-id="element-1506">
          <div className="px-4 py-2" style={{
          backgroundColor: bump1 ? GOLD : 'rgba(212,175,55,0.18)'
        }} data-id="element-1507">
            <span className="text-xs font-bold uppercase tracking-widest" style={{
            color: bump1 ? NAVY : GOLD,
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1508">
              ⚡ Special One-Time Offer
            </span>
          </div>
          <div className="px-4 pt-3 pb-4" data-id="element-1509">
            {/* Checkbox row */}
            <div className="flex items-start gap-3 mb-3 cursor-pointer" onClick={() => setBump1(!bump1)} data-id="element-1510">
              <div className="flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center transition-all duration-200" style={{
              borderColor: GOLD,
              backgroundColor: bump1 ? GOLD : 'transparent',
              minWidth: '24px',
              marginTop: '1px'
            }} data-id="element-1511">
                {bump1 && <CheckIcon className="h-4 w-4" style={{
                color: NAVY
              }} data-id="element-1512" />}
              </div>
              <div data-id="element-1513">
                <p className="font-bold text-white text-sm md:text-base leading-snug" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1514">
                  Add the Premium Hardcover Edition{' '}
                  <span style={{
                  color: GOLD
                }} data-id="element-1515">
                    — Only +${BUMP1_PRICE} Today
                  </span>
                </p>
                <p className="text-white/50 text-xs mt-0.5 italic" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1516">
                  Own it. Display it. Keep it forever.
                </p>
              </div>
            </div>
            {/* Image + bullets */}
            <div className="flex gap-3 items-start mb-3" data-id="element-1517">
              <div className="flex-shrink-0 relative cursor-pointer group" onClick={e => {
              e.stopPropagation();
              setBump1LightboxOpen(true);
            }} data-id="element-1518">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/pSD1kcciQ8RbkUdnckm8uJ/PREMIUM_EDITION_V1.2.png" alt="Premium Hardcover Edition — tap to enlarge" className="rounded-lg shadow-lg flex-shrink-0 w-[100px] h-[100px] md:w-[130px] md:h-[130px] object-cover transition-opacity group-hover:opacity-80" loading="lazy" decoding="async" data-id="element-1519" />
                {/* zoom hint */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-lg" data-id="element-1520">
                  <div className="bg-black/50 rounded-full p-1.5" data-id="element-1521">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-1522">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0zm-6-3v6m-3-3h6" data-id="element-1523" />
                    </svg>
                  </div>
                </div>
                <p className="text-center text-[9px] mt-1 leading-tight" style={{
                color: GOLD,
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1524">
                  Tap to enlarge
                </p>
              </div>
              <ul className="space-y-1.5 flex-1" data-id="element-1525">
                {BUMP1_ITEMS.map(item => <BumpItem key={item} text={item} data-id="element-1526" />)}
              </ul>
            </div>
            {/* CTA */}
            <div className="w-full text-center font-bold py-3 rounded-lg transition-all duration-200 text-sm cursor-pointer select-none" style={{
            backgroundColor: bump1 ? GOLD : 'transparent',
            border: `2px solid ${GOLD}`,
            color: bump1 ? NAVY : GOLD,
            fontFamily: 'Inter, sans-serif'
          }} onClick={() => setBump1(!bump1)} data-id="element-1527">
              {bump1 ? '✓ Added to my order' : `Add Premium Hardcover — Only +$${BUMP1_PRICE}`}
            </div>
          </div>
        </div>

        {/* ── ORDER BUMP 2 ── */}
        <div className="rounded-xl mb-5 overflow-hidden transition-all duration-300" style={{
        border: bump2 ? `3px solid ${GOLD}` : '3px solid rgba(212,175,55,0.4)',
        backgroundColor: bump2 ? 'rgba(212,175,55,0.07)' : '#0a1628',
        boxShadow: bump2 ? `0 0 28px rgba(212,175,55,0.3)` : '0 2px 12px rgba(0,0,0,0.3)'
      }} data-id="element-1528">
          <div className="px-4 py-2" style={{
          backgroundColor: bump2 ? GOLD : 'rgba(212,175,55,0.18)'
        }} data-id="element-1529">
            <span className="text-xs font-bold uppercase tracking-widest" style={{
            color: bump2 ? NAVY : GOLD,
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1530">
              🚀 Ultimate Upgrade
            </span>
          </div>
          <div className="px-4 pt-3 pb-4" data-id="element-1531">
            {/* Checkbox row */}
            <div className="flex items-start gap-3 mb-3 cursor-pointer" onClick={() => setBump2(!bump2)} data-id="element-1532">
              <div className="flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center transition-all duration-200" style={{
              borderColor: GOLD,
              backgroundColor: bump2 ? GOLD : 'transparent',
              minWidth: '24px',
              marginTop: '1px'
            }} data-id="element-1533">
                {bump2 && <CheckIcon className="h-4 w-4" style={{
                color: NAVY
              }} data-id="element-1534" />}
              </div>
              <div data-id="element-1535">
                <p className="font-bold text-white text-sm md:text-base leading-snug" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1536">
                  Upgrade to the Complete Collector's Bundle{' '}
                  <span style={{
                  color: GOLD
                }} data-id="element-1537">
                    — Only +${bump2Price} Today
                    {bump1 && <span className="ml-2 text-xs font-normal text-white/35 line-through" data-id="element-1538">
                        ${BUMP2_PRICE_STANDALONE}
                      </span>}
                  </span>
                </p>
                <p className="text-white/50 text-xs mt-0.5 italic" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1539">
                  Every volume. Every format. The full archive.
                </p>
              </div>
            </div>
            {/* Image + bullets */}
            <div className="flex gap-3 items-start mb-2" data-id="element-1540">
              {/* Clickable image → lightbox */}
              <div className="flex-shrink-0 relative cursor-pointer group" onClick={e => openLightbox(e, 0)} data-id="element-1541">
                <img src={BUNDLE_IMAGES[0]} alt="Time Traveler's Collector Bundle — tap to see all items" className="rounded-lg shadow-lg transition-opacity group-hover:opacity-80 w-[100px] h-[100px] md:w-[130px] md:h-[130px] object-cover" loading="lazy" decoding="async" data-id="element-1542" />
                {/* Thumbnail hint overlay */}
                <div className="absolute bottom-1 right-1 bg-black/70 rounded px-1 py-0.5 flex gap-0.5" data-id="element-1543">
                  {[1, 2, 3].map(i => <div key={i} className="rounded-sm overflow-hidden" style={{
                  width: '12px',
                  height: '12px'
                }} data-id="element-1544">
                      <img src={BUNDLE_IMAGES[i]} alt="" className="w-full h-full object-cover" decoding="async" data-id="element-1545" />
                    </div>)}
                  <span className="text-white text-[8px] ml-0.5 leading-none self-center" data-id="element-1546">
                    +{BUNDLE_IMAGES.length - 4}
                  </span>
                </div>
                {/* "Tap to see" label */}
                <p className="text-center text-[9px] mt-1 leading-tight" style={{
                color: GOLD,
                fontFamily: 'Inter, sans-serif',
                width: '100px'
              }} data-id="element-1547">
                  Tap to see all items
                </p>
              </div>
              <div className="flex-1" data-id="element-1548">
                <ul className="space-y-1.5" data-id="element-1549">
                  {BUMP2_ITEMS.slice(0, bump2Expanded ? BUMP2_ITEMS.length : 4).map(item => <BumpItem key={item} text={item} data-id="element-1550" />)}
                </ul>
                {BUMP2_ITEMS.length > 4 && <button type="button" className="flex items-center gap-1 text-[11px] mt-1.5" style={{
                color: GOLD,
                fontFamily: 'Inter, sans-serif',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0
              }} onClick={e => {
                e.stopPropagation();
                setBump2Expanded(!bump2Expanded);
              }} data-id="element-1551">
                    <ChevronDownIcon className={`h-3 w-3 transition-transform ${bump2Expanded ? 'rotate-180' : ''}`} data-id="element-1552" />
                    {bump2Expanded ? 'Show less' : `+${BUMP2_ITEMS.length - 4} more items`}
                  </button>}
              </div>
            </div>
            {/* CTA */}
            <div className="w-full text-center font-bold py-3 rounded-lg transition-all duration-200 text-sm cursor-pointer select-none mt-2" style={{
            backgroundColor: bump2 ? GOLD : 'transparent',
            border: `2px solid ${GOLD}`,
            color: bump2 ? NAVY : GOLD,
            fontFamily: 'Inter, sans-serif'
          }} onClick={() => setBump2(!bump2)} data-id="element-1553">
              {bump2 ? '✓ Upgrade added' : `Upgrade to Collector Bundle — Only +$${bump2Price}`}
            </div>
          </div>
        </div>

        {/* ── FALLBACK ── */}
        <div className="rounded-xl py-4 px-5 text-center" style={{
        backgroundColor: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)'
      }} data-id="element-1554">
          <p className="text-white/45 text-xs italic mb-3" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1555">
            Just want the core retrospective?
          </p>
          <div className="flex items-center justify-center gap-3 mb-3" data-id="element-1556">
            <img src="https://cdn.magicpatterns.com/uploads/ewSrEYsf3d6aAxsfRJbJ5B/BACK_IN_TIME_-_DIGITAL11v2.png" alt="Digital Edition" className="rounded-md" style={{
            width: '52px',
            height: '52px',
            objectFit: 'cover',
            opacity: 0.6
          }} loading="lazy" decoding="async" data-id="element-1557" />
            <div className="text-left" data-id="element-1558">
              <p className="text-white/55 text-sm font-medium" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1559">
                Digital Edition
              </p>
              <p className="text-white/45 text-sm" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1560">
                $39.97
              </p>
            </div>
          </div>
          <a href={buildUrl(CHECKOUT_BASIC)} onClick={() => track('Digital Edition', 39.97)} className="text-white/40 text-xs underline hover:text-white/55 transition-colors" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1561">
            Get the Digital Edition
          </a>
        </div>
      </div>

      {lightboxOpen && <ImageLightbox images={BUNDLE_IMAGES} startIndex={lightboxStart} onClose={() => setLightboxOpen(false)} data-id="element-1562" />}
      {bump1LightboxOpen && <ImageLightbox images={['https://uploadthingy.s3.us-west-1.amazonaws.com/pSD1kcciQ8RbkUdnckm8uJ/PREMIUM_EDITION_V1.2.png']} startIndex={0} onClose={() => setBump1LightboxOpen(false)} data-id="element-1563" />}
    </section>;
};
// ─── SECTION 6: EMOTIONAL CLOSE ───────────────────────────────────────────────
const EmotionalCloseV2 = () => <section style={{
  backgroundColor: NAVY,
  paddingTop: '48px',
  paddingBottom: '64px'
}} className="md:py-24" data-id="element-1564">
    <div className="container mx-auto px-6 md:px-10 max-w-2xl text-center" data-id="element-1565">
      <p className="text-sm italic mb-5" style={{
      color: GOLD,
      fontFamily: 'Inter, sans-serif'
    }} data-id="element-1566">
        Forty years went fast. The next forty will too.
      </p>
      <h2 className="text-[30px] md:text-5xl font-bold text-white mb-6 leading-tight" style={{
      fontFamily: "'Playfair Display', Georgia, serif"
    }} data-id="element-1567">
        Some stories fade.{' '}
        <span className="block" data-id="element-1568">Others become part of who you are.</span>
      </h2>
      <p className="text-base md:text-lg text-white/70 leading-relaxed mb-10 max-w-lg mx-auto" style={{
      fontFamily: 'Inter, sans-serif'
    }} data-id="element-1569">
        The question is whether you're the kind of person who documents what
        matters, or lets it fade into the algorithm.
      </p>
      <div className="max-w-md mx-auto" data-id="element-1570">
        <GoldButton href="#pricing-top" data-id="element-1571">
          CLAIM YOUR COMPLETE COLLECTION NOW →
        </GoldButton>
        <p className="text-white/35 text-xs mt-3 text-center" style={{
        fontFamily: 'Inter, sans-serif'
      }} data-id="element-1572">
          <span className="line-through" data-id="element-1573">$79.97</span> $59.97, Instant access.
          One decision.
        </p>
      </div>
    </div>
  </section>;
// ─── V2 HEADER ────────────────────────────────────────────────────────────────
const HeaderV2 = () => <header className="sticky top-0 z-50 border-b" style={{
  backgroundColor: '#060f22',
  borderColor: `${GOLD}30`
}} data-id="element-1574">
    <div className="container mx-auto px-6 py-4 flex justify-between items-center max-w-6xl" data-id="element-1575">
      <span className="font-bold text-lg" style={{
      color: GOLD,
      fontFamily: "'Playfair Display', Georgia, serif"
    }} data-id="element-1576">
        Back In Time
      </span>
      <a href="#pricing-top" className="cta font-bold text-sm px-5 py-2.5 rounded-lg transition-all hover:brightness-110" style={{
      backgroundColor: GOLD,
      color: NAVY,
      fontFamily: 'Inter, sans-serif',
      minHeight: '44px',
      display: 'flex',
      alignItems: 'center'
    }} data-id="element-1577">
        Get My Copy
      </a>
    </div>
  </header>;
export function LandingV2() {
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const clickId = urlParams.get('click_id');
    if (clickId) {
      try {
        sessionStorage.setItem('cf_click_id', clickId);
        localStorage.setItem('cf_click_id', clickId);
      } catch (e) {}
    }
  }, []);
  return <div className="w-full min-h-screen" style={{
    backgroundColor: NAVY
  }} data-id="element-1578">
      <HeaderV2 data-id="element-1579" />
      <main data-id="element-1580">
        <HeroV2 data-id="element-1581" />
        <ProblemV2 data-id="element-1582" />
        <WhatsInsideV2 data-id="element-1583" />
        <SocialProofV2 data-id="element-1584" />
        <PurchaseV2 data-id="element-1585" />
        <EmotionalCloseV2 data-id="element-1586" />
      </main>
      <Footer data-id="element-1587" />
    </div>;
}